import {
  AlignmentContractError,
} from "./errors";


export const ALIGNMENT_OPERATIONS = Object.freeze({
  LEFT: "left",
  CENTER_X: "center-x",
  RIGHT: "right",
  TOP: "top",
  CENTER_Y: "center-y",
  BOTTOM: "bottom",
});


export const DISTRIBUTION_OPERATIONS = Object.freeze({
  HORIZONTAL_CENTERS: "horizontal-centers",
  VERTICAL_CENTERS: "vertical-centers",
  HORIZONTAL_GAPS: "horizontal-gaps",
  VERTICAL_GAPS: "vertical-gaps",
});


export const ALIGNMENT_TARGETS = Object.freeze({
  SELECTION: "selection",
  FIRST: "first",
  LAST: "last",
  EXPLICIT: "explicit",
});


function finiteNumber(value, fieldName) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    throw new AlignmentContractError(
      `${fieldName} must be a finite number.`,
      "INVALID_ALIGNMENT_NUMBER",
    );
  }

  return number;
}


function nonEmptyString(value, fieldName) {
  if (typeof value !== "string" || value.trim() === "") {
    throw new AlignmentContractError(
      `${fieldName} must be a non-empty string.`,
      "INVALID_ALIGNMENT_ID",
    );
  }

  return value.trim();
}


function freezeObject(value, fieldName) {
  if (
    value === null
    || typeof value !== "object"
    || Array.isArray(value)
  ) {
    throw new AlignmentContractError(
      `${fieldName} must be an object.`,
      "INVALID_ALIGNMENT_OBJECT",
    );
  }

  return Object.freeze({
    ...value,
  });
}


export function createSelectionItem({
  id,
  left,
  right,
  top,
  bottom,
  centerX = null,
  centerY = null,
  width = null,
  height = null,
  locked = false,
  visible = true,
  metadata = {},
} = {}) {
  const normalizedLeft = finiteNumber(
    left,
    "SelectionItem.left",
  );
  const normalizedRight = finiteNumber(
    right,
    "SelectionItem.right",
  );
  const normalizedTop = finiteNumber(
    top,
    "SelectionItem.top",
  );
  const normalizedBottom = finiteNumber(
    bottom,
    "SelectionItem.bottom",
  );

  if (normalizedRight < normalizedLeft) {
    throw new AlignmentContractError(
      "SelectionItem.right must be greater than or equal to left.",
      "INVALID_HORIZONTAL_BOUNDS",
    );
  }

  if (normalizedBottom < normalizedTop) {
    throw new AlignmentContractError(
      "SelectionItem.bottom must be greater than or equal to top.",
      "INVALID_VERTICAL_BOUNDS",
    );
  }

  const normalizedWidth =
    width === null || width === undefined
      ? normalizedRight - normalizedLeft
      : finiteNumber(
          width,
          "SelectionItem.width",
        );

  const normalizedHeight =
    height === null || height === undefined
      ? normalizedBottom - normalizedTop
      : finiteNumber(
          height,
          "SelectionItem.height",
        );

  return Object.freeze({
    id: nonEmptyString(
      id,
      "SelectionItem.id",
    ),
    left: normalizedLeft,
    right: normalizedRight,
    top: normalizedTop,
    bottom: normalizedBottom,
    centerX:
      centerX === null || centerX === undefined
        ? (normalizedLeft + normalizedRight) / 2
        : finiteNumber(
            centerX,
            "SelectionItem.centerX",
          ),
    centerY:
      centerY === null || centerY === undefined
        ? (normalizedTop + normalizedBottom) / 2
        : finiteNumber(
            centerY,
            "SelectionItem.centerY",
          ),
    width: normalizedWidth,
    height: normalizedHeight,
    locked: Boolean(locked),
    visible: Boolean(visible),
    metadata: freezeObject(
      metadata,
      "SelectionItem.metadata",
    ),
  });
}


export function isSelectionItem(value) {
  return Boolean(
    value
    && typeof value === "object"
    && Object.isFrozen(value)
    && typeof value.id === "string"
    && value.id !== ""
    && Number.isFinite(value.left)
    && Number.isFinite(value.right)
    && Number.isFinite(value.top)
    && Number.isFinite(value.bottom)
    && Number.isFinite(value.centerX)
    && Number.isFinite(value.centerY)
    && Number.isFinite(value.width)
    && Number.isFinite(value.height)
    && typeof value.locked === "boolean"
    && typeof value.visible === "boolean"
  );
}


export function assertSelectionItem(value) {
  if (!isSelectionItem(value)) {
    throw new AlignmentContractError(
      "Expected a valid immutable SelectionItem.",
      "INVALID_SELECTION_ITEM",
    );
  }

  return value;
}


export function createTransformPatch({
  id,
  deltaX = 0,
  deltaY = 0,
  beforeBounds,
  afterBounds,
  metadata = {},
} = {}) {
  return Object.freeze({
    id: nonEmptyString(
      id,
      "TransformPatch.id",
    ),
    deltaX: finiteNumber(
      deltaX,
      "TransformPatch.deltaX",
    ),
    deltaY: finiteNumber(
      deltaY,
      "TransformPatch.deltaY",
    ),
    beforeBounds: freezeObject(
      beforeBounds,
      "TransformPatch.beforeBounds",
    ),
    afterBounds: freezeObject(
      afterBounds,
      "TransformPatch.afterBounds",
    ),
    metadata: freezeObject(
      metadata,
      "TransformPatch.metadata",
    ),
  });
}


export function createAlignmentResult({
  kind,
  operation,
  changed = false,
  patches = [],
  selectionBounds = null,
  spacing = null,
  metadata = {},
} = {}) {
  if (!Array.isArray(patches)) {
    throw new AlignmentContractError(
      "AlignmentResult.patches must be an array.",
      "INVALID_ALIGNMENT_PATCHES",
    );
  }

  return Object.freeze({
    kind: nonEmptyString(
      kind,
      "AlignmentResult.kind",
    ),
    operation: nonEmptyString(
      operation,
      "AlignmentResult.operation",
    ),
    changed: Boolean(changed),
    patches: Object.freeze([
      ...patches,
    ]),
    selectionBounds:
      selectionBounds === null
        ? null
        : freezeObject(
            selectionBounds,
            "AlignmentResult.selectionBounds",
          ),
    spacing:
      spacing === null || spacing === undefined
        ? null
        : finiteNumber(
            spacing,
            "AlignmentResult.spacing",
          ),
    metadata: freezeObject(
      metadata,
      "AlignmentResult.metadata",
    ),
  });
}


export function isAlignmentResult(value) {
  return Boolean(
    value
    && typeof value === "object"
    && Object.isFrozen(value)
    && typeof value.kind === "string"
    && typeof value.operation === "string"
    && typeof value.changed === "boolean"
    && Array.isArray(value.patches)
    && Object.isFrozen(value.patches)
  );
}


export function assertAlignmentResult(value) {
  if (!isAlignmentResult(value)) {
    throw new AlignmentContractError(
      "Expected a valid immutable AlignmentResult.",
      "INVALID_ALIGNMENT_RESULT",
    );
  }

  return value;
}


export function assertAlignmentOperation(operation) {
  if (!Object.values(ALIGNMENT_OPERATIONS).includes(operation)) {
    throw new AlignmentContractError(
      `Unsupported alignment operation: ${String(operation)}.`,
      "INVALID_ALIGNMENT_OPERATION",
    );
  }

  return operation;
}


export function assertDistributionOperation(operation) {
  if (!Object.values(DISTRIBUTION_OPERATIONS).includes(operation)) {
    throw new AlignmentContractError(
      `Unsupported distribution operation: ${String(operation)}.`,
      "INVALID_DISTRIBUTION_OPERATION",
    );
  }

  return operation;
}


export function assertAlignmentTarget(target) {
  if (!Object.values(ALIGNMENT_TARGETS).includes(target)) {
    throw new AlignmentContractError(
      `Unsupported alignment target: ${String(target)}.`,
      "INVALID_ALIGNMENT_TARGET",
    );
  }

  return target;
}
